import React, { useState, useEffect, useRef } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import { resolveDescription } from '../../util/listingDescription';
import { withViewport } from '../../util/uiHelpers';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  listingSlug as canonicalSlug,
  createCategoryForUrl,
  createSubCategoryForUrl,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  Page,
  NamedRedirect,
  LayoutSingleColumn,
  Breadcrumbs,
  IconLocation,
  IconArrowHead,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import {
  findCachedListingIdBySlug,
  sendInquiry,
  fetchTimeSlots,
  fetchTransactionLineItems,
  resetInquiry,
  setInitialValues,
} from './ListingPage.duck';
import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleToggleFavorites,
  priceForSchemaMaybe,
  handleSubmitInquiry,
  getItemConditionForSchema,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionGallery from './SectionGallery';
import SectionStatistics from './SectionStatistics.js';
import SectionTitlePrice from './SectionTitlePrice.js';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck.js';
import { LISTING_STATUS } from '../../constants/listing.constants.js';
import SectionLimitExceeded from './SectionLimitExceeded.js';
import { HtmlToReact } from './HtmlToReact.js';
import useInterfaceLang from '../../hooks/useInterfaceLang.js';
import { formatLocation } from '../../util/mapbox.js';
import SectionProviderLocation from './SectionProviderLocation.js';
import SectionDirectLinks from './SectionDirectLinks.js';
import SectionSpecifications from './SectionSpecifications.js';
import SectionDescription from './SectionDescription.js';
import SectionContactForm from './SectionContactForm.js';
import SectionEquipment from './SectionEquipment.js';
import { getEquipmentSummary, getSpecificationItems } from './listingSectionUtils';
import shareIcon from '../../components/IconShareNew/share-ios.svg';

import css from './ListingPage.module.css';
import classNames from 'classnames';

const fixHTML = htmlString => {
  return htmlString
    .replace(/＜/g, '<') // Replace full-width left angle bracket
    .replace(/＞/g, '>'); // Replace full-width right angle bracket
};

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

const breadcrumbsConfig = currentListing => {
  const publicData = currentListing.attributes.publicData;

  return [
    {
      label: <FormattedMessage id={`General.listing.category.${publicData.category}`} />,
      name: 'SearchPage',
      to: {
        search: `status=${LISTING_STATUS.FOR_SALE}&category=${publicData.category}`,
      },
      key: 'categoryForSale',
    },
    {
      label: (
        <FormattedMessage id={`General.listing.type.${publicData.category}.${publicData.type}`} />
      ),
      name: 'SearchPage',
      to: {
        // `type` takes the filter's `category/type` shape; a bare type names no
        // category (several are shared) and the search page ignores it.
        search: `status=${LISTING_STATUS.FOR_SALE}&type=${encodeURIComponent(
          `${publicData.category}/${publicData.type}`
        )}`,
      },
      key: 'categoryForSale',
    },
  ];
};

export const ListingPageComponent = props => {
  const { interfaceLang } = useInterfaceLang();

  const resolvedListingIdStr = props.params.id || props.listingPageId?.uuid;
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === resolvedListingIdStr
  );
  const [mounted, setMounted] = useState(false);
  const [focusSignal, setFocusSignal] = useState(false);
  const [favoriteSignal, setFavoriteSignal] = useState(false);

  const { viewport, ssrIsMobile } = props;
  const isMobile = viewport?.width > 0 ? viewport.width < 768 : (ssrIsMobile ?? false);

  useEffect(() => {
    setMounted(true);
    return () => {
      props.callSetInitialValues(setInitialValues, {});
    };
  }, []);

  const {
    currentUser,
    getListing,
    getOwnListing,
    intl,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    sendInquiryInProgress,
    sendInquiryError,
    sendInquiryReady,
    history,
    onSendInquiry,
    onResetInquiry,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    onUpdateFavorites,
    onManageDisableScrolling,
    isAuthenticated,
    reviews,
  } = props;

  const contactRef = useRef(null);

  const listingConfig = config.listing;
  // An SEO URL carries no id, so this is normally props.listingPageId — which only arrives
  // once the API has resolved the slug, and is why LoadingPage appears at all. cachedListingId
  // is that same answer found locally, and it goes first on purpose: a canonical slug belongs
  // to exactly one listing, so a match is definitionally right for this URL, whereas
  // listingPageId is deliberately left pointing at the *previous* listing while the next one
  // loads (see setInitialValues in ListingPage.duck).
  const listingId = rawParams.id
    ? new UUID(rawParams.id)
    : props.cachedListingId || props.listingPageId;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;

  if (!listingId) {
    const topbar = <TopbarContainer />;
    if (showListingError && showListingError.status === 404) {
      return <NotFoundPage staticContext={props.staticContext} />;
    } else if (showListingError && showListingError.status === 410) {
      if (props.staticContext) props.staticContext.gone = true;
      return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} staleListing />;
    } else if (showListingError && showListingError.status === 'closed') {
      return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} closedListing />;
    } else if (showListingError) {
      return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
    }
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));
  // The whole final segment, not a title slug plus a key: a listing whose legacy
  // address was preserved keeps answering there while its title is untouched, and this
  // helper is the single rule that decides which of the two addresses applies. The API
  // applies the same rule, so building the slug any other way here means redirecting a
  // listing off the very URL the migration preserved for it.
  // `ensureListing` returns a placeholder with `id: null` until the data arrives, so
  // fall back to the id from the route rather than building a slug from no uuid.
  const listingSlug = canonicalSlug(
    currentListing.id ? currentListing : { ...currentListing, id: listingId }
  );
  const category = createCategoryForUrl(currentListing.attributes.publicData?.category);
  const type = currentListing.attributes.publicData?.type;
  const subCategory = createSubCategoryForUrl(currentListing.attributes.publicData?.category, type);
  const params = { ...rawParams, slug: listingSlug, category, subCategory, id: listingId.uuid };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return (
      <NamedRedirect
        name="ListingPage"
        params={{ slug: listingSlug, category, subCategory }}
        search={location.search}
      />
    );
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError && showListingError.status === 410) {
    // Listing is stale / no longer available
    if (props.staticContext) props.staticContext.gone = true;
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} staleListing />;
  } else if (showListingError && showListingError.status === 'closed') {
    // Listing is closed
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} closedListing />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  // Send every request to the listing's one canonical address.
  //
  // Two cases, one rule: a uuid-based /l/:id URL has no slug at all, and an SEO URL may
  // carry a slug that is no longer current — a legacy address the listing has since
  // moved off after a retitle, or simply gibberish typed into the title half. Both are
  // "the requested segment is not the canonical one", so both redirect the same way.
  //
  // Two exclusions. The owner's own draft and pending-approval previews are not public
  // addresses — they carry an id and a variant, their slug segment is decorative, and
  // canonicalising them would send the owner to a public URL that does not exist yet.
  // And an unloaded listing has no address to canonicalise *to*, so redirecting on one
  // would bounce the request to a slug built from an empty title.
  const isOwnListingPreview = isDraftVariant || isPendingApprovalVariant || showOwnListingsOnly;
  const isUuidUrl = rawParams.id && !rawParams.category;
  const hasStaleSlug = !!rawParams.slug && rawParams.slug !== listingSlug;
  if (!isOwnListingPreview && currentListing.id && (isUuidUrl || hasStaleSlug)) {
    if (props.staticContext) {
      props.staticContext.redirectStatus = 301;
    }
    return <NamedRedirect name="ListingPage" params={{ slug: listingSlug, category, subCategory }} />;
  }

  const { price = null, title = '', publicData = {}, metadata = {} } = currentListing.attributes;

  // Not `attributes.description`: the text has moved to publicData, and the
  // attribute is left holding the title. The API's /listings/resolve patches
  // its own response, but the canonical (/l/:id) and draft routes fetch the
  // listing straight from Sharetribe, so resolving here covers every route.
  const description = resolveDescription(currentListing) || '';

  function stripYearFromTitle(title) {
    return title.replace(/\b(17|18|19|20)\d{2}\b$/, '').trim();
  }

  const displayTitle = stripYearFromTitle(title);

  function convertTextToHTML(text) {
    if (!text) return '';
    return text
      .replace(/\n{2,}/g, '</p><p>')
      .replace(/\n/g, '<br/>')
      .trim()
      .replace(/^/, '<p>')
      .concat('</p>');
  }

  const fixedHTMLDescription = description && fixHTML(description);
  const isHTML = /<\/?[a-z][\s\S]*>/i.test(fixedHTMLDescription);

  const finalDescription = isHTML ? fixedHTMLDescription : convertTextToHTML(description);
  const hasDescription = !!finalDescription;

  const richTitle = (
    <span>
      {richText(displayTitle, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const showSellerInfo =
    !isOwnListing &&
    currentListing?.attributes?.publicData?.status !== LISTING_STATUS.SOLD &&
    currentListing?.attributes?.publicData?.status !== LISTING_STATUS.CANCELED;

  const { listingType, transactionProcessAlias, unitType, location: listingLocation } = publicData;
  const language = interfaceLang.split('-')[0];

  const address =
    typeof listingLocation?.address === 'object'
      ? formatLocation(listingLocation.address, language)
      : listingLocation?.address;

  const { orderedItems: specificationItems } = getSpecificationItems({
    publicData,
    metadata,
    listingFieldConfigs: listingConfig.listingFields,
    categoryConfiguration: config.categoryConfiguration,
    status: publicData?.status,
    listingState: currentListing?.attributes?.state,
    condition: publicData?.condition,
    intl,
  });

  const hasSpecifications = specificationItems?.length > 0;

  const { hasEquipment } = getEquipmentSummary({
    publicData,
    metadata,
    listingFieldConfigs: listingConfig.listingFields,
    categoryConfiguration: config.categoryConfiguration,
  });

  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  // Fallback to inquiry process if transactionProcessAlias is missing
  // This allows existing listings without these fields to support inquiry transactions
  const effectiveTransactionProcessAlias = transactionProcessAlias || 'default-inquiry/release-1';
  const effectiveListingType = listingType || 'inquiry';
  const effectiveUnitType = unitType || 'inquiry';

  const processName = resolveLatestProcessName(effectiveTransactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? 'booking' : isPurchase ? 'purchase' : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && (processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected);

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };

  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  // For SectionContactForm, we create a custom handler that initiates a transaction after sending
  const onSubmitContactForm = values => {
    const listingId = new UUID(params.id);
    const listing = getListing(listingId);

    // Pass isAuthenticated so transaction creation can be skipped for guest users
    return onSendInquiry(listing, values, config, isAuthenticated).catch(() => {
      // Error handling is done in the duck file
    });
  };

  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
    config,
    isAuthenticated,
  });

  const onToggleFavorites = handleToggleFavorites({
    ...commonParams,
    currentUser,
    onUpdateFavorites,
    location,
    setFavoriteSignal,
  });

  const handleShare = () => {
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      navigator.share({ url: window.location.href }).catch(() => {});
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const strippedPathname = location.pathname.replace(/^\/m/, '');
  const productURL = `${config.marketplaceRootURL}${strippedPathname}${location.search}${location.hash}`;
  const schemaAvailability = currentListing.currentStock
    ? currentListing.currentStock.attributes.quantity > 0
      ? 'https://schema.org/InStock'
      : 'https://schema.org/OutOfStock'
    : 'https://schema.org/InStock'; // default fallback

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};

  const priceValidUntil = new Date();
  priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
  const priceValidUntilISO = priceValidUntil.toISOString().split('T')[0];

  // Get itemCondition for Offer schema (required for Google Merchant Listing)
  const itemCondition = getItemConditionForSchema(currentListing.attributes?.publicData);

  // Build review and aggregateRating schema fields from available reviews
  const reviewsWithRating = (reviews || []).filter(
    r => r.attributes?.rating && r.attributes?.content
  );
  const schemaReviewsMaybe =
    reviewsWithRating.length > 0
      ? {
          review: reviewsWithRating.map(r => ({
            '@type': 'Review',
            author: {
              '@type': 'Person',
              name: r.author?.attributes?.profile?.displayName || 'Anonymous',
            },
            reviewRating: {
              '@type': 'Rating',
              ratingValue: r.attributes.rating,
              bestRating: 5,
              worstRating: 1,
            },
            reviewBody: r.attributes.content,
            datePublished: r.attributes.createdAt?.toISOString?.()?.split('T')[0],
          })),
        }
      : {};
  const reviewsForAvg = (reviews || []).filter(r => r.attributes?.rating);
  const schemaAggregateRatingMaybe =
    reviewsForAvg.length > 0
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: (
              reviewsForAvg.reduce((sum, r) => sum + r.attributes.rating, 0) / reviewsForAvg.length
            ).toFixed(1),
            reviewCount: reviewsForAvg.length,
            bestRating: 5,
            worstRating: 1,
          },
        }
      : {};

  const scrollToContact = () => {
    if (!contactRef.current) {
      return;
    }
    const offset = 120;
    const top = contactRef.current.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const scrollToSection = id => {
    const element = document.getElementById(id);
    if (!element) {
      return;
    }
    const offset = 120;
    const top = element.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={metadata.metaDescription || finalDescription}
      noIndex={false}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: finalDescription,
        name: schemaTitle,
        image: schemaImages,
        ...(publicData?.manufacturer
          ? { brand: { '@type': 'Brand', name: publicData.manufacturer } }
          : {}),
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...availabilityMaybe,
          itemCondition: itemCondition,
          priceValidUntil: priceValidUntilISO,
        },
        ...schemaReviewsMaybe,
        ...schemaAggregateRatingMaybe,
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.contentWrapperForProductLayout}>
          <div className={css.contentWrapperForProductLayoutInner}>
            <div className={css.mainColumnForProductLayout}>
              <div className={classNames(css.contentThreeQuarter, css.titlePriceWrapper)}>
                <div className={css.breadcrumbsContainer}>
                  <Breadcrumbs
                    className={css.breadCrumbs}
                    links={breadcrumbsConfig(currentListing)}
                  />
                  {isMobile ? (
                    <button
                      type="button"
                      className={css.mobileShareButton}
                      aria-label={intl.formatMessage({ id: 'ListingPage.share' })}
                      onClick={handleShare}
                    >
                      <img
                        className={css.mobileShareIcon}
                        src={shareIcon}
                        alt=""
                        aria-hidden="true"
                      />
                    </button>
                  ) : null}
                </div>
                <SectionTitlePrice
                  listing={currentListing}
                  title={richTitle}
                  currentUser={currentUser}
                  onToggleFavorites={onToggleFavorites}
                  ssrIsMobile={ssrIsMobile}
                />
                <SectionLimitExceeded currentListing={currentListing} isOwnListing={isOwnListing} />
                {mounted && currentListing.id && noPayoutDetailsSetWithOwnListing ? (
                  <ActionBarMaybe
                    className={css.actionBarForProductLayout}
                    isOwnListing={isOwnListing}
                    listing={currentListing}
                    showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                    currentUser={currentUser}
                  />
                ) : null}
                {mounted && currentListing.id ? (
                  <ActionBarMaybe
                    className={css.actionBarForProductLayout}
                    isOwnListing={isOwnListing}
                    listing={currentListing}
                    currentUser={currentUser}
                    editParams={{
                      id: listingId.uuid,
                      slug: listingSlug,
                      type: listingPathParamType,
                      tab: listingTab,
                    }}
                  />
                ) : null}
              </div>

              <div className={css.mobileGalleryOrder}>
                <SectionGallery
                  listing={currentListing}
                  variantPrefix={config.layout.listingImage.variantPrefix}
                  currentUser={currentUser}
                  onToggleFavorites={isOwnListing ? undefined : onToggleFavorites}
                  ssrIsMobile={ssrIsMobile}
                />
              </div>

              <SectionProviderLocation
                providerName={authorDisplayName}
                providerProfile={currentListing?.author?.attributes?.profile}
                address={address}
                onContactClick={scrollToContact}
                listing={currentListing}
                onManageDisableScrolling={onManageDisableScrolling}
              />

              <div className={css.contentThreeQuarter}>
                <SectionDirectLinks
                  onGoToDescription={() => scrollToSection('listing-description')}
                  onGoToSpecifications={() => scrollToSection('listing-specifications')}
                  onGoToEquipment={() => scrollToSection('listing-equipment')}
                  showDescription={hasDescription}
                  showSpecifications={hasSpecifications}
                  showEquipment={hasEquipment}
                />
                <SectionLimitExceeded
                  currentListing={currentListing}
                  isOwnListing={isOwnListing}
                  className={css.limitExceededMobile}
                />
                <div className={css.info}>
                  {hasDescription ? (
                    <SectionDescription
                      text={
                        !finalDescription ? (
                          <FormattedMessage id="ListingPage.noDescription" />
                        ) : (
                          <HtmlToReact htmlString={finalDescription} />
                        )
                      }
                    />
                  ) : null}
                  {hasSpecifications ? (
                    <SectionSpecifications
                      publicData={publicData}
                      metadata={metadata}
                      listingFieldConfigs={listingConfig.listingFields}
                      categoryConfiguration={config.categoryConfiguration}
                      status={publicData?.status}
                      listingState={currentListing?.attributes?.state}
                      condition={publicData?.condition}
                    />
                  ) : null}
                  {hasEquipment ? (
                    <SectionEquipment
                      publicData={publicData}
                      metadata={metadata}
                      categoryConfiguration={config.categoryConfiguration}
                      listingFieldConfigs={listingConfig.listingFields}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={css.contactFormFullBleed}>
          <div className={css.contactFormInner}>
            <div className={css.contactFormContent}>
              <div ref={contactRef} className={css.contactForm}>
                <SectionContactForm
                  onSubmit={onSubmitInquiry}
                  onResetInquiry={onResetInquiry}
                  focusSignal={focusSignal}
                  inProgress={sendInquiryInProgress}
                  error={sendInquiryError}
                  ready={sendInquiryReady}
                  listingTitle={currentListing?.attributes?.title}
                  providerName={authorDisplayName}
                  initialValues={{
                    subject: currentListing?.attributes?.title,
                    senderName: currentUser?.attributes?.profile?.displayName,
                    senderEmail: currentUser?.attributes?.email,
                    senderPhoneNumber: currentUser?.attributes?.profile?.protectedData?.phoneNumber,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponentWithViewport
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const ListingPageComponentWithViewport = withViewport(ListingPageComponent);

const mapStateToProps = (state, ownProps) => {
  const { isAuthenticated } = state.auth;
  const {
    id: listingPageId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    sendInquiryInProgress,
    sendInquiryError,
    sendInquiryReady,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    listingPageId,
    cachedListingId: findCachedListingIdBySlug(state, ownProps?.params?.slug),
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    ssrIsMobile: state.ui.ssrIsMobile,
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
    sendInquiryReady,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, body, config, isAuthenticated) =>
    dispatch(sendInquiry(listing, body, config, isAuthenticated)),
  onResetInquiry: () => dispatch(resetInquiry()),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
  onUpdateFavorites: payload => dispatch(updateProfile(payload)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedListingPage);

export default ListingPage;
